import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { promptChainApi } from '@/services/promptChainApi';
import { executionPolling } from '@/utils/pollingService';
import {
  applyHighlights,
  applyHighlightsWhenReady,
  removeHighlights,
  resetHighlightTooltips,
} from '@rm-ui/levelling/aiHighlight';
import { parseUnitEvidence } from '@rm-ui/levelling/rationaleDraft';
import { sourceTextHash } from '@rm-ui/levelling/sourceTextHash';

const DEFAULT_PREFETCH_AHEAD = 2;

export function useClassificationAiAssist() {
  const self = {
    factorStates: ref({}),
  };

  // --- UI state ---
  self.showAiAssist = ref(true)
  self.showHighlights = ref(self.showAiAssist.value)
  self.aiAssistDisplayMode = ref(localStorage.getItem('aiAssistDisplayMode') || 'full')
  self.aiDocSummaryRef = ref(null)

  // Cancels a pending 'apply once the document has rendered' watch.
  let _cancelPendingHighlights = null

  // Non-reactive stored references (set once per classification run)
  
  let _getText = null;
  let _systemReference = null;

  const _units = ref([]);
  const _currentUnitKey = ref(null);
  const _currentUnitIndex = ref(0);
  const _viewState = ref(null);
  const _chainId = ref(null);
  const _panelViewStates = ref([]);
  const _toggleViewStates = ref([]);
  const _keepStates = ref([]);
  const _filterMode = ref('classification');
  const _alignmentScoreMin = ref(4);
  const _centralityScoreMin = ref(4);
  const _prefetchAhead = ref(DEFAULT_PREFETCH_AHEAD);

  // --- Private helpers ---

  function _buildSubFactorsText(unit) {
    return (unit.subFactors || [])
      .map(sf => `${sf.name} - ${sf.description}`)
      .join('\n');
  }

  function _stateKey(unitKey, chainId = _chainId.value) {
    return `${chainId}::${unitKey}`;
  }

  function _updateFactorState(stateKey, patch) {
    const current = self.factorStates.value[stateKey] || {};
    self.factorStates.value = {
      ...self.factorStates.value,
      [stateKey]: { ...current, ...patch }
    };
  }

  function _collectOutputs(execution) {
    const allOutputs = {};
    if (execution.steps) {
      execution.steps.forEach(step => {
        if (step.outputs) {
          for (const [key, value] of Object.entries(step.outputs)) {
            if (typeof value === 'string') {
              try { allOutputs[key] = JSON.parse(value); } catch { allOutputs[key] = value; }
            } else {
              allOutputs[key] = value;
            }
          }
        }
      });
    }
    return allOutputs;
  }

  function _handlePollResponse(stateKey, response) {
    if (!response.data?.success) return {};
    const execution = response.data.data;

    switch (execution.execution_status) {
      case 'queued':
      case 'running':
        return {};
      case 'completed': {
        const outputs = _collectOutputs(execution);
        _updateFactorState(stateKey, {
          state: 'completed',
          result: {
            outputs,
            executionDetails: {
              chain_name: execution.chain_name,
              total_steps: execution.progress?.total_steps || 0,
              completed_steps: execution.progress?.completed_steps || 0,
            }
          }
        });
        return { shouldStop: true, data: outputs };
      }
      case 'failed': {
        _updateFactorState(stateKey, {
          state: 'failed',
          error: execution.error_message || 'Execution failed'
        });
        return { shouldStop: true };
      }
      default:
        return {};
    }
  }

  async function _bulkResolveExisting() {
    if (!_systemReference || !_chainId.value) return new Set();
    const resolved = new Set();
    // Only runs against the CURRENT source text are reusable. A role whose job
    // description was replaced (a new upload, an edited system document) must
    // not inherit the previous document's evidence — that showed as "N matches
    // found" with nothing to highlight, and would ground an AI rationale on
    // text the evaluator cannot see. No text yet → nothing can match.
    const textHash = sourceTextHash(_getText ? _getText() : null);
    if (!textHash) return resolved;
    try {
      const resp = await promptChainApi.getExecutionsBySystemRef(_chainId.value, _systemReference, textHash);
      if (!resp.data?.success || !resp.data.data?.length) return resolved;

      const fetches = resp.data.data.map(async (entry) => {
        try {
          const statusResp = await promptChainApi.getExecutionStatus(entry.execution_id);
          if (statusResp.data?.success) {
            const execution = statusResp.data.data;
            const stateKey = _stateKey(entry.factor);
            if (execution.execution_status === 'completed') {
              const outputs = _collectOutputs(execution);
              _updateFactorState(stateKey, {
                state: 'completed',
                executionId: entry.execution_id,
                result: {
                  outputs,
                  executionDetails: {
                    chain_name: execution.chain_name,
                    total_steps: execution.progress?.total_steps || 0,
                    completed_steps: execution.progress?.completed_steps || 0,
                  }
                }
              });
              resolved.add(entry.factor);
            } else if (['queued', 'running'].includes(execution.execution_status)) {
              // A run for this unit is already underway (a reload mid-analysis,
              // or another tab). Adopt it and poll, rather than paying for a
              // duplicate that would finish later than the one in progress.
              _updateFactorState(stateKey, { state: 'executing', executionId: entry.execution_id, result: null, error: null });
              executionPolling.startPolling(
                entry.execution_id,
                async () => _handlePollResponse(stateKey, await promptChainApi.getExecutionStatus(entry.execution_id)),
                () => {},
                () => _updateFactorState(stateKey, { state: 'failed', error: 'Lost connection while polling for status' })
              );
              resolved.add(entry.factor);
            }
          }
        } catch (err) {
          console.warn(`Failed to fetch outputs for ${entry.factor}:`, err);
        }
      });
      await Promise.all(fetches);
    } catch (error) {
      console.warn('Bulk resolve check failed:', error);
    }
    return resolved;
  }

  async function _triggerOne(unitKey) {
    if (!_chainId.value) return;

    const stateKey = _stateKey(unitKey);

    _updateFactorState(stateKey, { state: 'executing', executionId: null, result: null, error: null });

    const unit = _units.value ? _units.value.find(u => u.key === unitKey) : null;
    const factorName = unit ? unit.factorName : unitKey;
    const subFactorsText = unit ? _buildSubFactorsText(unit) : '';
    const text = _getText ? _getText() : null;

    if (!text) {
      console.warn(`AI assist: text not ready for ${unitKey}, will retry on next ensurePrefetched`);
      const { [stateKey]: _, ...rest } = self.factorStates.value;
      self.factorStates.value = rest;
      return;
    }

    try {
      const resp = await promptChainApi.triggerChain({
        prompt_chain_id: _chainId.value,
        source_data: {
          custom: {
            system_reference: _systemReference,
            factor: unitKey,
            // Identity of the analysed text, so a later session only reuses
            // this run when the document is still the same.
            text_hash: sourceTextHash(text)
          }
        },
        manual_values: {
          text,
          factor_name: factorName,
          sub_factors: subFactorsText
        }
      });

      if (!resp.data.success) {
        _updateFactorState(stateKey, { state: 'failed', error: resp.data.error || 'Failed to start chain execution' });
        return;
      }

      const executionId = resp.data.execution_id;
      _updateFactorState(stateKey, { executionId });

      executionPolling.startPolling(
        executionId,
        async () => {
          const statusResp = await promptChainApi.getExecutionStatus(executionId);
          return _handlePollResponse(stateKey, statusResp);
        },
        () => {},
        (error) => {
          console.error(`AI assist polling error for ${unitKey}:`, error);
          _updateFactorState(stateKey, { state: 'failed', error: 'Lost connection while polling for status' });
        }
      );
    } catch (error) {
      console.error(`Error triggering AI assist for ${unitKey}:`, error);
      _updateFactorState(stateKey, {
        state: 'failed',
        error: error.response?.data?.error || error.message || 'Failed to execute chain'
      });
    }
  }

  function _ensureTriggered(unitKey) {
    if (self.factorStates.value[_stateKey(unitKey)]) return;
    _triggerOne(unitKey);
  }

  // --- Computed: derived AI state ---

  self.showAiAssistPanel = computed(() =>
    self.showAiAssist.value && _panelViewStates.value.includes(_viewState.value)
  )

  self.showAiAssistToggle = computed(() =>
    _toggleViewStates.value.includes(_viewState.value)
  )

  self.currentFactorAiState = computed(() => {
    const key = _currentUnitKey.value
    return key ? (self.factorStates.value[_stateKey(key)] || null) : null
  })

  self.highlightedSentences = computed(() => {
    const state = self.currentFactorAiState.value
    if (!state || !state.result) return []
    const outputs = state.result.outputs
    if (!outputs || !outputs.result) return []
    let parsed
    try {
      parsed = typeof outputs.result === 'string' ? JSON.parse(outputs.result) : outputs.result
    } catch {
      return []
    }
    const evidence = (parsed && parsed.result && parsed.result.evidence) || []
    return evidence.filter(x => {
      if (x.sub_factor === 'None') return false
      if ((x.sentence || '').length < 30) return false
      if (_filterMode.value === 'evaluation') {
        return Number(x.alignment_score) >= _alignmentScoreMin.value &&
          Number(x.centrality_score) >= _centralityScoreMin.value
      }
      return ['Direct', 'Focused'].includes(x.alignment) &&
        ['Central', 'Dominant'].includes(x.centrality)
    })
  })

  // --- Highlight DOM logic ---
  //
  // The matching and DOM-splitting algorithm lives in `@rm-ui/levelling/aiHighlight`
  // so rm-portal and rm-portal-beta highlight identically. It was extracted from
  // here verbatim; this composable now only supplies the container and the
  // sentences, and owns the watchers that decide *when* to re-apply.

  function _applyHighlightsToDOM() {
    applyHighlights(self.aiDocSummaryRef.value, self.highlightedSentences.value)
  }

  function _applyHighlightsWhenReady() {
    if (_cancelPendingHighlights) {
      _cancelPendingHighlights()
      _cancelPendingHighlights = null
    }
    _cancelPendingHighlights = applyHighlightsWhenReady(
      self.aiDocSummaryRef.value,
      self.highlightedSentences.value,
    )
  }

  function _removeHighlightsFromDOM() {
    if (_cancelPendingHighlights) {
      _cancelPendingHighlights()
      _cancelPendingHighlights = null
    }
    removeHighlights(self.aiDocSummaryRef.value)
  }

  function _shouldApplyHighlights() {
    return self.showHighlights.value && self.aiAssistDisplayMode.value === 'full'
  }

  // --- Watchers ---

  watch(self.highlightedSentences, () => {
    if (_shouldApplyHighlights()) nextTick(_applyHighlightsWhenReady)
  })

  watch(self.aiAssistDisplayMode, (val) => {
    localStorage.setItem('aiAssistDisplayMode', val)
    if (val === 'full' && self.showHighlights.value) nextTick(_applyHighlightsWhenReady)
    else _removeHighlightsFromDOM()
  })

  watch(self.showHighlights, (val) => {
    if (val && self.aiAssistDisplayMode.value === 'full') {
      nextTick(_applyHighlightsWhenReady)
    } else {
      _removeHighlightsFromDOM()
    }
  })

  watch(self.showAiAssistPanel, (visible) => {
    if (visible && _shouldApplyHighlights() && self.highlightedSentences.value.length) {
      nextTick(_applyHighlightsWhenReady)
    }
  })

  // --- Public methods ---

  self.toggleAiAssist = (val) => {
    self.showAiAssist.value = val
    if (val) {
      self.showHighlights.value = true
    } else {
      self.showHighlights.value = false
    }
  }

  // The reuse-existing-runs lookup in flight, if any. Every caller of
  // startClassification follows it with ensurePrefetched WITHOUT awaiting, so
  // the prefetch used to see an empty state and start fresh executions before
  // the lookup had a chance to say "already done" — a duplicate paid run of
  // every unit on every reload. Triggers now wait for the lookup to settle.
  let _pendingResolve = null;

  self.startClassification = async (getText, systemReference) => {
    self.resetAll();
    _getText = getText;
    _systemReference = systemReference;

    _pendingResolve = _bulkResolveExisting();
    let resolved;
    try {
      resolved = await _pendingResolve;
    } finally {
      _pendingResolve = null;
    }

    for (let i = 0; i < Math.min(_prefetchAhead.value, _units.value.length); i++) {
      if (!resolved.has(_units.value[i].key)) {
        _ensureTriggered(_units.value[i].key);
      }
    }
  };

  self.ensurePrefetched = async (currentIndex) => {
    if (_pendingResolve) await _pendingResolve;
    for (let i = currentIndex; i < Math.min(currentIndex + _prefetchAhead.value, _units.value.length); i++) {
      _ensureTriggered(_units.value[i].key);
    }
  };

  self.getFactorState = (unitKey) => {
    return self.factorStates.value[_stateKey(unitKey)] || null;
  };

  /**
   * The UNFILTERED evidence for a unit, for AI rationale drafting (PD-1301) —
   * the draft ranks and trims it itself (rationaleDraft.selectEvidence), so the
   * panel's alignment/centrality thresholds must not apply here.
   * Returns { ready, failed, items } so a caller can wait for a still-running
   * analysis or give up on a failed one.
   */
  self.rawEvidenceFor = (unitKey) => {
    const state = unitKey ? self.getFactorState(unitKey) : null;
    if (!state) return { ready: false, failed: false, items: [] };
    const ready = state.state === 'completed';
    return { ready, failed: state.state === 'failed', items: ready ? parseUnitEvidence(state) : [] };
  };

  /** Start the analysis for a unit if it has not run — the evaluator may have
   *  hidden the assist panel, and a draft still needs its evidence. */
  self.ensureUnitTriggered = async (unitKey) => {
    if (_pendingResolve) await _pendingResolve;
    if (unitKey) _ensureTriggered(unitKey);
  };

  self.resetAll = () => {
    for (const entry of Object.values(self.factorStates.value)) {
      if (entry.executionId) {
        executionPolling.stopPolling(entry.executionId);
      }
    }
    self.factorStates.value = {};
    _getText = null;
    _systemReference = null;
  };

  /**
   * Connect the composable to the parent component's reactive state.
   * Call once during setup to wire up watchers that react to navigation.
   */
  self.connectToView = ({
    units,
    currentUnitName,
    currentUnitIndex,
    viewState,
    chainId,
    filterMode,
    alignmentScoreMin,
    centralityScoreMin,
    prefetchAhead,
    panelViewStates,
    toggleViewStates,
    keepStates,
  }) => {
    _panelViewStates.value = panelViewStates || []
    _toggleViewStates.value = toggleViewStates || []
    _keepStates.value = keepStates || []

    watch(units, (v) => { _units.value = v || [] }, { immediate: true })
    watch(currentUnitName, (v) => { _currentUnitKey.value = v }, { immediate: true })
    watch(currentUnitIndex, (v) => { _currentUnitIndex.value = v }, { immediate: true })
    watch(viewState, (v) => { _viewState.value = v }, { immediate: true })
    if (chainId) watch(chainId, (v) => { _chainId.value = v }, { immediate: true })
    if (filterMode) watch(filterMode, (v) => { _filterMode.value = v || 'classification' }, { immediate: true })
    if (alignmentScoreMin) watch(alignmentScoreMin, (v) => { _alignmentScoreMin.value = Number(v) || 4 }, { immediate: true })
    if (centralityScoreMin) watch(centralityScoreMin, (v) => { _centralityScoreMin.value = Number(v) || 4 }, { immediate: true })
    if (prefetchAhead) watch(prefetchAhead, (v) => { _prefetchAhead.value = Math.max(1, Number(v) || DEFAULT_PREFETCH_AHEAD) }, { immediate: true })

    watch(currentUnitIndex, (idx) => {
      self.aiAssistDisplayMode.value = 'full'
      self.ensurePrefetched(idx)
    })

    watch(() => self.showAiAssistPanel.value, (visible) => {
      if (visible) self.ensurePrefetched(_currentUnitIndex.value)
    })

    watch(viewState, (newState) => {
      if (!_keepStates.value.includes(newState)) {
        self.resetAll()
      }
    })
  }

  // --- Cleanup ---
  onBeforeUnmount(() => {
    self.resetAll();
    // The tooltip is appended to <body>, so it outlives this component's subtree
    // unless it is removed explicitly.
    resetHighlightTooltips();
  });

  return self;
}
